import type { ComponentType, ReactNode } from 'react';

/** What `React.lazy` / `lazyChunk` take: a dynamic import that resolves to `{ default: Component }`. */
export type ComponentLoader<P = object> = () => Promise<{ default: ComponentType<P> }>;

/**
 * One route a module mounts under `/services`, declared by the module (its `route.ts`) and
 * mounted by `src/app/service-modules.ts` + `ServicesPage`. Plain data plus a lazy loader: a
 * `route.ts` must not import its console, or the console would land in the entry chunk.
 */
export interface ServiceRoute {
  /** Relative to `/services`, as React Router takes it (`vod/*`, `waap/:serviceId/*`). */
  path: string;
  /** The console / page. Its chunk is its own: nothing but this loader is paid for up front. */
  load: ComponentLoader;
  /** Renders around `load`'s component (an entitlement or workspace gate), itself lazy. */
  gate?: ComponentLoader<{ children: ReactNode }>;
  /** `false` leaves the route out of the table (a build-time flag). Omitted means mounted. */
  enabled?: boolean;
}

/**
 * A CDN provider's console: `/services/cdn/:serviceId/*` renders the one whose `provider`
 * matches the service's provider (the controlplane's `provider_name`, lower-cased).
 */
export interface CdnProviderConsole {
  provider: string;
  load: ComponentLoader;
}

/** The catalog categories (`SERVICE_CATEGORIES` keys in `service-categories.ts`, which owns their order and icons). */
export type ServiceCategoryKey =
  | 'streaming'
  | 'cdn'
  | 'compute'
  | 'kubernetes'
  | 'databases'
  | 'storage'
  | 'observability'
  | 'management'
  | 'security'
  | 'mail';

/**
 * What the service catalog needs from the module that serves a provider scope: which category a
 * provisioned service of that scope falls under, and where it opens. A module declares it in its
 * `service.ts` (`serviceScopes`, plain `as const` data that imports no shared name: the root checks
 * the shape against this type), built from its own `paths.ts`; `src/app/service-scopes.ts` merges them.
 * Plain data: no console import. A scope with no `route` has no console of its own here (the shared
 * CDN shell opens `cdn` scopes; anything else falls back to the dashboard category filter).
 */
export interface ServiceScope {
  /** The controlplane's provider scope (`External.VMail`). */
  scope: string;
  category: ServiceCategoryKey;
  /** Where a provisioned service of this scope opens, as a function of its id. */
  route?: (serviceId: string) => string;
}

const SERVICES_PREFIX = '/services/';

/**
 * A module's mount base (`paths.waap.base`) as the path React Router takes
 * inside the services hub (`waap`), with an optional tail (`servicePath(base, ':serviceId/*')`).
 * A `route.ts` builds its `path` from this, so the route and every link to it share one string.
 */
export function servicePath(base: string, tail?: string): string {
  if (!base.startsWith(SERVICES_PREFIX)) throw new Error(`servicePath: ${base} is not under /services`);
  const relative = base.slice(SERVICES_PREFIX.length);
  return tail ? `${relative}/${tail}` : relative;
}
